import apiService from './apiService';

// TypeScript interfaces
export interface QuotationItemTemplate {
  id: string;
  companyId: string;
  itemCode: string;
  description: string;
  category?: string;
  subcategory?: string;  // Subcategory for options (e.g., "Extra Storage", "Value-added Service")
  unit?: string;
  unitPrice: number;
  taxRate?: number;
  discountType?: 'fixed' | 'percentage';
  discount?: number;
  accountId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  account?: {
    id: string;
    code: string;
    name: string;
    accountType: string;
  };
}

export interface CreateQuotationItemTemplateInput {
  itemCode: string;
  description: string;
  category?: string;
  subcategory?: string;
  unitPrice: number;
  taxRate?: number;
  discountType?: 'fixed' | 'percentage';
  discount?: number;
  accountId?: string;
}

export interface UpdateQuotationItemTemplateInput {
  itemCode?: string;
  description?: string;
  category?: string;
  subcategory?: string;
  unit?: string;
  unitPrice?: number;
  taxRate?: number;
  discountType?: 'fixed' | 'percentage';
  discount?: number;
  accountId?: string;
  isActive?: boolean;
}

export interface QuotationItemTemplateFilters {
  category?: string;
  isActive?: boolean;
  searchTerm?: string;
}

export interface QuotationItemTemplateStats {
  totalTemplates: number;
  activeTemplates: number;
  inactiveTemplates: number;
  categoriesCount: number;
}

class QuotationItemTemplateService {
  private baseUrl = '/accounting/quotation-item-templates';

  /**
   * Get all quotation item templates with optional filters
   */
  async getAllTemplates(filters?: QuotationItemTemplateFilters): Promise<QuotationItemTemplate[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
      if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);

      const queryString = params.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

      const response: any = await apiService.get(url);
      return response.data || response || [];
    } catch (error) {
      console.error('Failed to get quotation item templates:', error);
      return [];
    }
  }

  /**
   * Get active quotation item templates only
   */
  async getActiveTemplates(): Promise<QuotationItemTemplate[]> {
    try {
      const response: any = await apiService.get(`${this.baseUrl}/active`);
      return response.data || response || [];
    } catch (error) {
      console.error('Failed to get active templates:', error);
      return [];
    }
  }

  /**
   * Get quotation item template by ID
   */
  async getTemplateById(id: string): Promise<QuotationItemTemplate> {
    const response: any = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data || response;
  }

  /**
   * Create a new quotation item template
   */
  async createTemplate(data: CreateQuotationItemTemplateInput): Promise<QuotationItemTemplate> {
    const response: any = await apiService.post(this.baseUrl, data);
    return response.data || response;
  }

  /**
   * Update an existing quotation item template
   */
  async updateTemplate(id: string, data: UpdateQuotationItemTemplateInput): Promise<QuotationItemTemplate> {
    const response: any = await apiService.put(`${this.baseUrl}/${id}`, data);
    return response.data || response;
  }

  /**
   * Delete a quotation item template
   */
  async deleteTemplate(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate a quotation item template
   */
  async activateTemplate(id: string): Promise<QuotationItemTemplate> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/activate`);
    return response.data || response;
  }

  /**
   * Deactivate a quotation item template
   */
  async deactivateTemplate(id: string): Promise<QuotationItemTemplate> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/deactivate`);
    return response.data || response;
  }

  /**
   * Get all categories
   */
  async getCategories(): Promise<string[]> {
    try {
      const response: any = await apiService.get(`${this.baseUrl}/categories`);
      return response.data || response || [];
    } catch (error) {
      console.error('Failed to get categories:', error);
      return [];
    }
  }

  /**
   * Get template statistics
   */
  async getStats(): Promise<QuotationItemTemplateStats> {
    try {
      const response: any = await apiService.get(`${this.baseUrl}/stats`);
      return response.data || response || {
        totalTemplates: 0,
        activeTemplates: 0,
        inactiveTemplates: 0,
        categoriesCount: 0
      };
    } catch (error) {
      console.error('Failed to get stats:', error);
      return {
        totalTemplates: 0,
        activeTemplates: 0,
        inactiveTemplates: 0,
        categoriesCount: 0
      };
    }
  }
}

const quotationItemTemplateService = new QuotationItemTemplateService();
export default quotationItemTemplateService;